import { useEffect, useState } from "react";
import axios from "axios";
import {
  Wallet, Send, ShoppingBag, TrendingUp, Trophy, MessageSquare,
  CheckCircle2, Clock, Ban, ShieldCheck, PlusCircle, SquarePen,
} from "lucide-react";
import GraficoRosca from "../sms/GraficoRosca";
import GraficoLinha from "../sms/GraficoLinha";
import AgendadasWhatsApp from "./AgendadasWhatsApp";
import { PainelCarregando } from "../Carregando";

/**
 * Painel do DISPARO EM MASSA via WhatsApp (Cloud API oficial) — MESMO FORMATO do
 * painel de SMS (4 KPIs + taxa de entrega + andamento + últimas + campanhas +
 * volume). Só marketing (decisão do dono). Envio real via template aprovado +
 * carteira compartilhada (R$ 0,399/msg). Liberado por loja (setting
 * whatsapp_disparo_disponivel) enquanto a API oficial não sai pra todos.
 */
const auth = () => {
  const token = localStorage.getItem("token");
  return token ? { headers: { Authorization: `Bearer ${token}` } } : {};
};
const brl = (v) =>
  `R$ ${(Number(v) || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// `onAbrirCampanha("prontos" | "criar")`: a campanha é uma PÁGINA (DisparosPage
// troca o conteúdo), não popup — pedido do dono, por espaço.
export default function PainelWhatsApp({ onAbrirCampanha }) {
  const [d, setD] = useState(null);
  const [erro, setErro] = useState(null);

  useEffect(() => {
    axios.get("/api/whatsapp-broadcast/dashboard", auth())
      .then((r) => setD(r.data))
      .catch(() => setErro("Não foi possível carregar o painel."));
  }, []);

  if (erro) return <div className="p-6 text-destructive">{erro}</div>;
  if (!d) return <PainelCarregando />;

  const env = d.envio, fat = d.faturamento;
  const enviados = env.enviados || 0;
  const receitaPorMsg = enviados ? Math.trunc((fat.receita_centavos / enviados)) / 100 : null;

  const kpis = [
    {
      rotulo: "Carteira", icone: Wallet, cor: "text-emerald-500 bg-emerald-500/10", borda: "border-t-emerald-500",
      valor: brl(0), valor2: `${(d.base_optin_total || 0).toLocaleString("pt-BR")} mensagens`,
      legenda: "gasto no período / base com opt-in",
    },
    {
      rotulo: "Mensagens enviadas", icone: Send, cor: "text-blue-500 bg-blue-500/10", borda: "border-t-blue-500",
      valor: enviados.toLocaleString("pt-BR"), valor2: brl((fat.custo_centavos || 0) / 100),
      legenda: "mensagens enviadas / valor investido nelas",
    },
    {
      rotulo: "Pedidos influenciados", icone: ShoppingBag, cor: "text-violet-500 bg-violet-500/10", borda: "border-t-violet-500",
      valor: (fat.pedidos || 0).toLocaleString("pt-BR"), valor2: brl((fat.receita_centavos || 0) / 100),
      legenda: "pedidos que vieram do link / faturamento deles",
    },
    {
      rotulo: "Receita por mensagem", icone: TrendingUp, cor: "text-amber-500 bg-amber-500/10", borda: "border-t-amber-500",
      valor: receitaPorMsg === null ? "—" : brl(receitaPorMsg),
      valor2: fat.roi_centesimos == null ? "sem ROI ainda" : `${(fat.roi_centesimos / 100).toFixed(1).replace(".0", "")}x`,
      legenda: "faturamento por mensagem / retorno sobre o investido",
    },
  ];

  return (
    <div className="space-y-5">
      {/* Como funciona */}
      <div className="flex items-start gap-3 rounded-xl border border-emerald-300/60 bg-emerald-50 dark:bg-emerald-950/20 p-4">
        <ShieldCheck className="w-5 h-5 text-emerald-500 mt-0.5 shrink-0" />
        <div className="text-sm flex-1">
          <p className="font-semibold text-emerald-700 dark:text-emerald-300">Disparo via API oficial do WhatsApp</p>
          <p className="text-muted-foreground">
            Toda mensagem de marketing no WhatsApp passa pela aprovação da Meta. Escreva a sua
            mensagem (ou use um modelo pronto), espere aprovar e dispare pra quem autorizou receber.
            O custo (R$ 0,399 por mensagem) sai da mesma carteira do SMS e da Ligação.
          </p>
        </div>
      </div>

      {/* Ações */}
      <div className="flex items-center justify-end gap-2">
        <button onClick={() => onAbrirCampanha?.("criar")}
          className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold hover:bg-muted">
          <SquarePen className="w-4 h-4" /> Escrever mensagem
        </button>
        <button onClick={() => onAbrirCampanha?.("prontos")}
          className="inline-flex items-center gap-2 rounded-lg bg-primary text-primary-foreground px-4 py-2 text-sm font-semibold">
          <PlusCircle className="w-4 h-4" /> Nova campanha
        </button>
      </div>

      <AgendadasWhatsApp />

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {kpis.map((k) => (
          <div key={k.rotulo} className={`rounded-xl border border-t-2 ${k.borda} bg-card p-4`}>
            <div className="flex items-start justify-between gap-2">
              <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{k.rotulo}</p>
              <span className={`rounded-lg p-1.5 ${k.cor}`}><k.icone className="w-4 h-4" /></span>
            </div>
            <p className="text-2xl font-bold mt-2 tabular-nums">
              {k.valor}<span className="text-muted-foreground font-semibold"> / {k.valor2}</span>
            </p>
            <p className="text-[11px] text-muted-foreground mt-1">{k.legenda}</p>
          </div>
        ))}
      </div>

      {/* 3 colunas */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold mb-4">Taxa de entrega</h3>
          <GraficoRosca rotuloTotal="mensagens" rotuloResto="Outros status"
            centroValor={`${((env.taxa_entrega_decimos || 0) / 10).toFixed(0)}%`} centroRotulo="de entrega"
            itens={[
              { id: "entregue", nome: "Total entregue", valor: env.entregues || 0 },
              { id: "nao-entregue", nome: "Total não entregue", valor: env.falhas || 0 },
            ]} />
        </div>

        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold mb-4">Andamento dos envios</h3>
          <p className="text-4xl font-bold tabular-nums">
            {((env.entregues || 0) + (env.falhas || 0) + (env.na_fila || 0)).toLocaleString("pt-BR")}
          </p>
          <p className="text-xs text-muted-foreground mt-1">mensagens no total</p>
          <div className="mt-4 space-y-2.5 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4 shrink-0 text-emerald-500" />
              <span className="text-muted-foreground flex-1">Enviadas</span>
              <span className="tabular-nums font-semibold">{((env.entregues || 0) + (env.falhas || 0)).toLocaleString("pt-BR")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 shrink-0 text-amber-500" />
              <span className="text-muted-foreground flex-1">Pendentes de envio</span>
              <span className="tabular-nums font-semibold">{(env.na_fila || 0).toLocaleString("pt-BR")}</span>
            </div>
            <div className="flex items-center gap-2">
              <Ban className="w-4 h-4 shrink-0 text-amber-500" />
              <span className="text-muted-foreground flex-1">Pediram para sair</span>
              <span className="tabular-nums font-semibold">{(env.optouts || 0).toLocaleString("pt-BR")}</span>
            </div>
          </div>
        </div>

        <div className="rounded-xl border bg-card p-5">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <MessageSquare className="w-4 h-4 text-muted-foreground" /> Últimas mensagens
          </h3>
          {!d.ultimas_mensagens.length ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Nada enviado ainda.</p>
          ) : (
            <ul className="divide-y -my-2">
              {d.ultimas_mensagens.slice(0, 6).map((m, i) => (
                <li key={i} className="py-2 text-sm truncate">{m.cliente_nome || m.telefone}</li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* Campanhas */}
      <div className="rounded-xl border bg-card p-5">
        <h3 className="font-semibold mb-4 flex items-center gap-2">
          <Trophy className="w-4 h-4 text-amber-500" /> Campanhas que mais renderam
        </h3>
        {!d.ranking.length ? (
          <p className="text-sm text-muted-foreground py-8 text-center">
            Nenhuma campanha enviada ainda. Clique em "Nova campanha" para montar a primeira.
          </p>
        ) : null}
      </div>

      <GraficoLinha dias={d.volume_por_dia} titulo="Volume de envios" />

    </div>
  );
}
